<template>
  <b-card v-if="render">
    <h3>{{ $t('serviceCategories.editHeaderTitle') }}</h3>
    <layout-loading v-if="itemLoading" />
    <!-- form -->
    <b-form class="mt-2">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="service-category-title"
          >
            <b-form-input
              id="service-category-title"
              v-model="form.title"
              :state="validateState('title')"
              aria-describedby="input-title-feedback"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="service-category-slug"
          >
            <b-form-input
              id="service-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
              aria-describedby="input-slug-feedback"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="service-category-language"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.priority')"
            label-for="services-categories-sort"
          >
            <b-form-input
              id="services-categories-sort"
              v-model.number="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.tags')"
            label-for="service-category-tags"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="service-category-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="service-categories-keywords"
          >
            <b-form-input
              id="service-categories-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="service-lang"
          >
            <b-form-textarea
              id="service-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="service-category-content"
            class="mb-2"
          >
            <SunEditorBasic
              id="service-category-content"
              ref="sunEditor"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="serviceCatThumbnail"
              v-model="form.serviceCatThumbnail"
              :placeholder="$t('general.select')"
              :state="validateState('serviceCatThumbnail')"
              :state-alt="validateState('serviceCatThumbnailAlt')"
              :alt.sync="form.serviceCatThumbnailAlt"
              :alt-validator="$v.form.serviceCatThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-service-cat-thumbnail-feedback"
                  :label="$t('general.thumbnail')"
                  :validator="$v.form.serviceCatThumbnail"
                  :is-bootstrap="false"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="service-cat-cover-image"
              v-model="form.serviceCatCoverImage"
              :placeholder="$t('general.select')"
              :state="validateState('serviceCatCoverImage')"
              :state-alt="validateState('serviceCatCoverImageAlt')"
              :alt.sync="form.serviceCatCoverImageAlt"
              :alt-validator="$v.form.serviceCatCoverImageAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-service-cat-cover-image-feedback"
                  :label="$t('general.cover')"
                  :validator="$v.form.serviceCatCoverImage"
                  :is-bootstrap="false"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t('general.isSubmit') : $t('general.update') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t('general.back') }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
    <!--/ form -->
  </b-card>
</template>

<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import '@/components/img-with-no-image-error.vue'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength } from 'vuelidate/lib/validators'
import SunEditorBasic from '@/components/SunEditorBasic/index.vue'
import useServiceCategoriesList from './useServiceCategoriesList'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    SunEditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  setup() {
    const {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchItem,
      fetchServiceCategories,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      serviceCategories,
      tags,
      updateItem,
    } = useServiceCategoriesList()
    return {
      addItem,
      addNewTag,
      addTag,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchItem,
      fetchServiceCategories,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      serviceCategories,
      tags,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.fetchItem(this.itemId)
    this.fetchTags('fa')
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
          minLength: minLength(3),
          isUnique: () => this.isUniqueSlug,
        },
        tags: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        description: {
          required,
          minLength: minLength(10),
        },
        content: {
          required,
          minLength: minLength(10),
        },
        serviceCatCoverImage: {
          required,
          isValidImageSize,
        },
        serviceCatCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
        serviceCatThumbnail: {
          required,
          isValidImageSize,
        },
        serviceCatThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  methods: {
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(this.item, selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    submitForm() {
      this.$refs.sunEditor.sendValToForm()
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['serviceCatCoverImage', 'serviceCatThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        sort: form.sort,
        tagIds: this.createTagIdArray(form.tags, this.newTagIds),
        keywords: form.keywords,
        description: form.description,
        content: form.content,
        serviceCatCoverImageFileId: form.serviceCatCoverImage ? form.serviceCatCoverImage._id : null,
        serviceCatThumbnailFileId: form.serviceCatThumbnail ? form.serviceCatThumbnail._id : null,
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>
